// src/components/manager/ManagerSystemNotifications.js
import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { FaBell, FaFilter, FaSearch } from 'react-icons/fa';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { classifyRoleGroup } from '../../utils/notificationMetadata';
import './ManagerSystemNotifications.css';

const DATE_FILTERS = [
    { value: 'all', label: 'Todas las fechas' },
    { value: '24h', label: 'Últimas 24 horas' },
    { value: '7d', label: 'Últimos 7 días' },
    { value: '30d', label: 'Últimos 30 días' },
];

const ROLE_LABELS = {
    administrador: 'Administradores',
    usuario: 'Usuarios',
    'sin-clasificar': 'Sin clasificar',
};

const DETALLES_DICT = {
    montoMensual: 'Cuota Mensual',
    corteDia: 'Día de Corte',
    sancionesInicioDia: 'Día de Sanciones',
    cuotaExtras: 'Cuotas Extraordinarias',
    interesesEnabled: 'Intereses Moratorios',
    interesesType: 'Tipo de Interés',
    interesesValue: 'Valor de Interés',
    interesesMesesPermitidosDeuda: 'Meses Permitidos',
    multasEnabled: 'Multas Automáticas',
    multas: 'Reglas de Multa',
    restriccionServiciosEnabled: 'Restricción de Servicios',
    restriccionMeses: 'Meses para Restricción',
    restriccionServicios: 'Servicios Restringidos',
    consumosExtra: 'Consumos Individualizados',
};

const DETALLES_ORDER = [
    'montoMensual',
    'corteDia',
    'sancionesInicioDia',
    'cuotaExtras',
    'interesesEnabled',
    'interesesType',
    'interesesValue',
    'interesesMesesPermitidosDeuda',
    'multasEnabled',
    'multas',
    'restriccionServiciosEnabled',
    'restriccionMeses',
    'restriccionServicios',
    'consumosExtra'
];

const toDate = (value) => {
    if (!value) {
        return null;
    }
    if (typeof value.toDate === 'function') {
        return value.toDate();
    }
    if (typeof value === 'number') {
        return new Date(value);
    }
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : new Date(parsed);
    }
    if (value instanceof Date) {
        return value;
    }
    return null;
};

const formatDateTime = (date) => {
    if (!(date instanceof Date)) {
        return '';
    }
    return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatLabel = (value) => {
    if (!value || typeof value !== 'string') {
        return '';
    }
    return value
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .map((chunk) => (chunk ? chunk.charAt(0).toUpperCase() + chunk.slice(1) : ''))
        .join(' ')
        .trim();
};

const ManagerSystemNotifications = ({ fraccionamientoId: externalFraccionamientoId = null, selectorFraccionamiento, hideHeader = false }) => {
    const { userData } = useAuth();
    const fraccionamientoId = externalFraccionamientoId || userData?.fraccionamientoId || null;

    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedItem, setExpandedItem] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('7d');
    const [roleFilters, setRoleFilters] = useState({
        administrador: true,
        usuario: true,
        'sin-clasificar': true,
    });

    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    // Resetear a la pagina 1 cuando cambie cualquier filtro o el fraccionamiento
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, categoryFilter, dateFilter, roleFilters, fraccionamientoId]);

    useEffect(() => {
        const isGlobalMode = !fraccionamientoId && userData?.rol === 'platformadmin';

        if (!fraccionamientoId && !isGlobalMode) {
            setNotifications([]);
            setIsLoading(false);
            return undefined;
        }

        setIsLoading(true);
        const ref = fraccionamientoId
            ? collection(db, 'fraccionamientos', fraccionamientoId, 'notificaciones')
            : collection(db, 'notificacionesPlataformaGlobal');
        const q = query(ref, orderBy('fecha', 'desc'), limit(100));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map((docSnap) => {
                const data = docSnap.data() || {};
                const fecha = toDate(data.fecha);
                const rawRole = data.actorRol || data.creadoPorRol || '';
                const roleGroup = data.actorRolGrupo || classifyRoleGroup(rawRole);

                return {
                    id: docSnap.id,
                    titulo: data.titulo || 'Notificación',
                    descripcion: data.descripcion || '',
                    categoria: data.categoria || 'General',
                    fecha,
                    actorNombre: data.actorNombre || data.creadoPorNombre || null,
                    actorRol: rawRole || null,
                    actorRolGrupo: roleGroup,
                    tipoMovimiento: data.tipoMovimiento || null,
                    origen: data.origen || null,
                    detalles: data.detalles || null,
                };
            });

            setNotifications(items);
            setIsLoading(false);
            setError('');
        }, (listenerError) => {
            console.error('ManagerSystemNotifications: error al escuchar notificaciones', listenerError);
            setError('No se pudieron cargar las notificaciones del sistema.');
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId, userData]);

    const availableCategories = useMemo(() => {
        const unique = new Set();
        notifications.forEach((item) => {
            if (item.categoria) {
                unique.add(item.categoria);
            }
        });
        return Array.from(unique).sort((a, b) => a.localeCompare(b));
    }, [notifications]);

    const roleOptions = useMemo(() => {
        const base = new Set(['administrador', 'usuario']);
        if (notifications.some((item) => (item.actorRolGrupo || 'sin-clasificar') === 'sin-clasificar')) {
            base.add('sin-clasificar');
        }
        return Array.from(base);
    }, [notifications]);

    const dateThreshold = useMemo(() => {
        if (dateFilter === 'all') {
            return null;
        }
        const now = Date.now();
        const map = {
            '24h': 24 * 60 * 60 * 1000,
            '7d': 7 * 24 * 60 * 60 * 1000,
            '30d': 30 * 24 * 60 * 60 * 1000,
        };
        const delta = map[dateFilter];
        return typeof delta === 'number' ? new Date(now - delta) : null;
    }, [dateFilter]);

    const filteredNotifications = useMemo(() => {
        const needle = searchTerm.trim().toLowerCase();

        return notifications.filter((item) => {
            if (dateThreshold && item.fecha instanceof Date) {
                if (item.fecha < dateThreshold) {
                    return false;
                }
            }

            const group = item.actorRolGrupo || 'sin-clasificar';
            if (roleFilters[group] === false) {
                return false;
            }

            if (categoryFilter !== 'all' && item.categoria !== categoryFilter) {
                return false;
            }

            if (!needle) {
                return true;
            }

            const haystack = [
                item.titulo,
                item.descripcion,
                item.actorNombre,
                item.actorRol,
                item.tipoMovimiento,
                item.origen,
                item.categoria,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return haystack.includes(needle);
        });
    }, [notifications, dateThreshold, roleFilters, categoryFilter, searchTerm]);

    const totalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE);

    const paginatedNotifications = useMemo(() => {
        const pageToUse = Math.max(1, Math.min(currentPage, totalPages || 1));
        const startIndex = (pageToUse - 1) * ITEMS_PER_PAGE;
        return filteredNotifications.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredNotifications, currentPage, totalPages]);

    const totalCount = filteredNotifications.length;
    const startItem = totalCount === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);

    const handleToggleRole = (role) => {
        setRoleFilters((prev) => ({
            ...prev,
            [role]: !(prev[role] !== false),
        }));
    };

    const isGlobalMode = !fraccionamientoId && userData?.rol === 'platformadmin';

    if (!fraccionamientoId && !isGlobalMode) {
        return (
            <section className="manager-system-notifications">
                {!hideHeader && (
                    <header className="manager-system-notifications__header">
                        <h2><FaBell /> Notificaciones del Sistema</h2>
                        <p>No se pudo identificar el fraccionamiento activo.</p>
                    </header>
                )}
            </section>
        );
    }

    return (
        <section className="manager-system-notifications">
            {!hideHeader && (
                <header className="manager-system-notifications__header">
                    <div className="manager-system-notifications__header-title">
                        <h2><FaBell /> Notificaciones del Sistema</h2>
                        <p>Monitorea la actividad de administradores y usuarios en tiempo real.</p>
                    </div>
                    {selectorFraccionamiento && (
                        <div className="manager-system-notifications__header-selector">
                            {selectorFraccionamiento}
                        </div>
                    )}
                </header>
            )}

            <div className="manager-system-notifications__filters">
                <div className="filter-group-main">
                    <label className="manager-system-notifications__search" htmlFor="managerNotificationsSearch">
                        <FaSearch aria-hidden="true" />
                        <input
                            id="managerNotificationsSearch"
                            type="search"
                            placeholder="Buscar por título, descripción o persona..."
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                        />
                    </label>

                    <div className="filter-selects-row">
                        <label className="manager-system-notifications__select" htmlFor="managerNotificationsDate">
                            <span>Fecha</span>
                            <select
                                id="managerNotificationsDate"
                                value={dateFilter}
                                onChange={(event) => setDateFilter(event.target.value)}
                            >
                                {DATE_FILTERS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </label>

                        <label className="manager-system-notifications__select" htmlFor="managerNotificationsCategory">
                            <span>Categoría</span>
                            <select
                                id="managerNotificationsCategory"
                                value={categoryFilter}
                                onChange={(event) => setCategoryFilter(event.target.value)}
                            >
                                <option value="all">Todas</option>
                                {availableCategories.map((category) => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>

                <fieldset className="manager-system-notifications__roles">
                    <legend>Filtro de Roles</legend>
                    <div className="roles-checkbox-grid">
                        {roleOptions.map((role) => (
                            <label key={role} className="role-checkbox-item">
                                <input
                                    type="checkbox"
                                    checked={roleFilters[role] !== false}
                                    onChange={() => handleToggleRole(role)}
                                />
                                <span>{ROLE_LABELS[role] || formatLabel(role)}</span>
                            </label>
                        ))}
                    </div>
                </fieldset>
            </div>

            <div className="manager-system-notifications__body">
                {isLoading && <p className="manager-system-notifications__status">Cargando notificaciones…</p>}
                {!isLoading && error && <p className="manager-system-notifications__status error">{error}</p>}
                {!isLoading && !error && filteredNotifications.length === 0 && (
                    <p className="manager-system-notifications__status">No se encontraron notificaciones con los filtros seleccionados.</p>
                )}
                {!isLoading && !error && filteredNotifications.length > 0 && (
                    <>
                        <ul className="manager-system-notifications__list">
                            {paginatedNotifications.map((item) => (
                                <li key={item.id} className="manager-system-notifications__item">
                                    <div className="manager-system-notifications__item-header">
                                        <h3>{item.titulo}</h3>
                                        {item.fecha && (
                                            <time dateTime={item.fecha.toISOString()}>{formatDateTime(item.fecha)}</time>
                                        )}
                                    </div>
                                    {item.descripcion && (
                                        <p className="manager-system-notifications__item-description">{item.descripcion}</p>
                                    )}
                                    <div className="manager-system-notifications__meta">
                                        {item.actorNombre && (
                                            <span className="manager-system-notifications__chip" title={item.actorRol ? formatLabel(item.actorRol) : undefined}>
                                                {item.actorNombre}
                                            </span>
                                        )}
                                        <span className={`manager-system-notifications__role manager-system-notifications__role--${item.actorRolGrupo}`}>
                                            {ROLE_LABELS[item.actorRolGrupo] || formatLabel(item.actorRolGrupo)}
                                        </span>
                                        {item.categoria && (
                                            <span className="manager-system-notifications__chip">{item.categoria}</span>
                                        )}
                                        {item.origen && (
                                            <span className="manager-system-notifications__chip muted">{formatLabel(item.origen)}</span>
                                        )}
                                        {item.tipoMovimiento && (
                                            <span className="manager-system-notifications__chip muted">{formatLabel(item.tipoMovimiento)}</span>
                                        )}
                                    </div>
                                    {item.detalles && (
                                        <div className="manager-system-notifications__details">
                                            <button 
                                                className="manager-system-notifications__details-btn"
                                                onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                                            >
                                                {expandedItem === item.id ? 'Ocultar Detalles' : 'Ver Detalles'}
                                            </button>
                                            {expandedItem === item.id && (
                                                <div className="manager-system-notifications__details-content">
                                                    {item.detalles?.resumen ? (
                                                        <ul className="manager-system-notifications__summary-list">
                                                            {DETALLES_ORDER.map((key) => {
                                                                if (item.detalles.resumen[key] === undefined || item.detalles.resumen[key] === '') return null;
                                                                const value = item.detalles.resumen[key];
                                                                const isModified = Array.isArray(item.detalles.modificados) && item.detalles.modificados.includes(key);
                                                                const valStr = typeof value === 'boolean' ? (value ? 'Activado' : 'Desactivado') : String(value);
                                                                return (
                                                                    <li key={key}>
                                                                        <strong>{DETALLES_DICT[key] || key}{isModified ? ' *' : ''}:</strong> 
                                                                        <span>{valStr}</span>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    ) : (
                                                        <pre>
                                                            {JSON.stringify(item.detalles, null, 2)}
                                                        </pre>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>

                        <div className="manager-system-notifications__pagination">
                            <div className="pagination-info">
                                Mostrando {startItem} a {endItem} de {totalCount} notificaciones
                            </div>
                            {totalPages > 1 && (
                                <div className="pagination-buttons">
                                    <button
                                        className="pagination-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                    >
                                        Anterior
                                    </button>
                                    <span className="pagination-current">
                                        Página {currentPage} de {totalPages}
                                    </span>
                                    <button
                                        className="pagination-btn"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default ManagerSystemNotifications;
